import { ZodError } from "zod";
import { HALLIDAY_DOMAIN, WIDGET_HEIGHT, WIDGET_WIDTH } from "@shared/constants";
import { MessageType, PaymentCategoryOrder, PaymentsWidgetQueryParams } from "./types";
import { PaymentStatus } from "../paymentsApiClient/types";

/**
 * Normalize the payment category order to ensure 'deposit' is always present.
 * - If the order is undefined, return undefined (no ordering specified).
 * - If the order already contains 'deposit', return as-is (client explicitly ranked it).
 * - Otherwise, prepend 'deposit' to the front (force to top).
 */
export const normalizePaymentCategoryOrder = (
    order: PaymentCategoryOrder | undefined
): PaymentCategoryOrder | undefined => {
    if (!order) return undefined;
    if (order.includes("deposit")) return order;
    return ["deposit", ...order];
};

// Initial height for embedded widget (must match skeleton height for smooth transition)
export const EMBED_INITIAL_HEIGHT = 400;

/**
 * Creates a loading skeleton for EMBED mode.
 * Shows an animated pulse effect while the widget loads.
 * Called at the very start of openHallidayPayments for immediate visual feedback.
 * 
 * @param targetElement - The DOM element to append the skeleton to
 * @param backgroundColor - Optional custom background color/gradient
 * @returns The skeleton element (for cleanup reference)
 */
export const createEmbedSkeleton = (
    targetElement: HTMLElement,
    backgroundColor?: string
): HTMLDivElement => {
    // Remove any existing skeleton first
    const existingSkeleton = document.getElementById("halliday-payments-skeleton");
    if (existingSkeleton) {
        existingSkeleton.remove();
    }
    
    // Set up target element for overlay positioning
    if (targetElement.style) {
        targetElement.style.position = "relative";
    }
    
    // Create wrapper if it doesn't exist
    let wrapper = document.getElementById("halliday-payments-wrapper");
    if (!wrapper) {
        wrapper = document.createElement("div");
        wrapper.id = "halliday-payments-wrapper";
        wrapper.style.cssText = `
            position: relative;
            width: ${WIDGET_WIDTH}px;
            max-width: 100%;
            display: block;
        `;
        targetElement.appendChild(wrapper);
    }
    
    const skeletonBg = backgroundColor || "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)";
    
    const skeleton = document.createElement("div");
    skeleton.id = "halliday-payments-skeleton";
    skeleton.style.cssText = `
        width: 100%;
        height: ${EMBED_INITIAL_HEIGHT}px;
        background: ${skeletonBg};
        background-size: 200% 100%;
        animation: halliday-skeleton-pulse 1.5s ease-in-out infinite;
        border-radius: 12px;
        position: relative;
        z-index: 1;
        transition: opacity 0.3s ease-out;
    `;
    
    // Add keyframes if not already present
    if (!document.getElementById("halliday-skeleton-styles") && document.head) {
        try {
            const style = document.createElement("style");
            style.id = "halliday-skeleton-styles";
            style.textContent = `
                @keyframes halliday-skeleton-pulse {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `;
            document.head.appendChild(style);
        } catch {
            // Ignore in test environments
        }
    }
    
    wrapper.appendChild(skeleton);
    return skeleton;
};

/**
 * Fades out and removes the skeleton.
 * Called when widget signals it's ready via WIDGET_READY message.
 */
export const removeEmbedSkeleton = (): void => {
    const skeleton = document.getElementById("halliday-payments-skeleton");
    if (skeleton) {
        skeleton.style.opacity = "0";
        setTimeout(() => skeleton.remove(), 300);
    }
};

/**
 * Serialize the query params to a base64 string.
 *
 * @param {PaymentsWidgetQueryParams} data {{@link PaymentsWidgetQueryParams}} The query params to serialize.
 * @returns The serialized (base64) query params
 */
export const serializeQueryParams = (data: PaymentsWidgetQueryParams): string => {
    const validatedData = PaymentsWidgetQueryParams.parse(data);
    return btoa(JSON.stringify(validatedData));
};

/**
 * Deserialize the query params from a base64 string.
 *
 * @param {string} serialized The base64 string to deserialize.
 * @returns {{@link PaymentsWidgetQueryParams}} The deserialized query params.
 */
export const deserializeQueryParams = (serialized: string): PaymentsWidgetQueryParams => {
    const validatedData = PaymentsWidgetQueryParams.parse(JSON.parse(atob(serialized)));
    return validatedData;
};

/**
 * Get the URL for the payments widget.
 *
 * @param {PaymentsWidgetQueryParams & { windowOrigin?: string }} params {{@link PaymentsWidgetQueryParams}} The query params to serialize.
 * @returns The URL for the payments widget.
 */
export const getPaymentsWidgetUrl = (
    params: PaymentsWidgetQueryParams & {
        windowOrigin?: string;
    }
): string => {
    const { windowOrigin = HALLIDAY_DOMAIN, ...queryParams } = params;
    const dataString = serializeQueryParams(queryParams);

    const widgetVersion = queryParams.widgetVersion;
    if (widgetVersion) {
        try {
            const version = parseInt(widgetVersion);
            return `${windowOrigin}/v${version}/payments/quote?data=${dataString}`;
        } catch (error) {
            console.error("Error parsing widget version", error);
        }
    }

    return `${windowOrigin}/payments/quote?data=${dataString}`;
};

/**
 * Open the payments widget.
 * Handles both popup and iframe modes. Serializes the query params and opens the widget in the appropriate mode.
 * Note: Skeleton loading is handled by openHallidayPayments before this function is called.
 *
 * @param {PaymentsWidgetQueryParams & { windowOrigin?: string }} params {{@link PaymentsWidgetQueryParams}} The query params to serialize.
 * @returns The window object for the payments widget.
 */
export const _openPaymentsWidget = (
    params: PaymentsWidgetQueryParams & {
        windowOrigin?: string;
    }
): Window | null => {
    // On mobile, the width and height are ignored. The popup will be a normal full screen tab.
    const width = WIDGET_WIDTH;
    const height = WIDGET_HEIGHT;
    const windowWidth = window.innerWidth;

    const left = windowWidth / 2 - width / 2;
    const top = 70;

    const { windowType = "POPUP", targetElementId, windowOrigin } = params;

    const widgetUrl = getPaymentsWidgetUrl(params);

    let paymentsWindow: Window | null;

    if (windowType === "EMBED") {
        const targetElement = document.getElementById(targetElementId as string);
        if (!targetElement) {
            throw new Error(`Element with id ${targetElementId} not found`);
        }
        
        let iframe = document.getElementById("halliday-payments-embed") as HTMLIFrameElement;
        const existingLocation = iframe ? iframe.src : null;
        
        if (existingLocation === widgetUrl) {
            iframe.style.display = "block";
        } else {
            // Skeleton is already created by openHallidayPayments at this point
            // Just need to create/update the iframe
            
            // Get or create wrapper (may have been created by createEmbedSkeleton)
            let wrapper = document.getElementById("halliday-payments-wrapper");
            if (!wrapper) {
                // Clear container and create wrapper
                targetElement.innerHTML = "";
                if (targetElement.style) {
                    targetElement.style.position = "relative";
                }
                
                wrapper = document.createElement("div");
                wrapper.id = "halliday-payments-wrapper";
                wrapper.style.cssText = `
                    position: relative;
                    width: ${WIDGET_WIDTH}px;
                    max-width: 100%;
                    display: block;
                `;
                targetElement.appendChild(wrapper);
            }
            
            // Create iframe - position absolute to overlay the skeleton
            iframe = document.createElement("iframe");
            iframe.id = "halliday-payments-embed";
            iframe.src = widgetUrl;
            iframe.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                border: none;
                display: block;
            `;
            iframe.setAttribute("scrolling", "no");
            
            // Append iframe to wrapper (it will overlay the skeleton)
            wrapper.appendChild(iframe);
        }

        paymentsWindow = iframe.contentWindow;

        const messageHandler = (event: MessageEvent) => {
            const { type, payload } = event.data || {};
            const isFromWidget = event.source === paymentsWindow;
            const isTrustedOrigin = event.origin === windowOrigin;

            // Always require the message to come from the same iframe window
            if (!isFromWidget) {
                return;
            }

            // Allow resize events from any origin, but require trusted origin for sensitive events
            if (!isTrustedOrigin && type !== MessageType.EVENT_RESIZE) {
                return;
            }

            if (type === MessageType.EVENT_WINDOW_CLOSE) {
                window.removeEventListener("message", messageHandler);
                const embedIframe = document.getElementById("halliday-payments-embed");
                if (embedIframe) {
                    embedIframe.style.display = "none";
                }
            } else if (type === MessageType.EVENT_RESIZE) {
                // Update skeleton height (controls wrapper height, iframe uses height: 100%)
                const skeleton = document.getElementById("halliday-payments-skeleton");
                const wrapper = document.getElementById("halliday-payments-wrapper");
                if (payload?.height !== undefined) {
                    if (skeleton) {
                        skeleton.style.height = payload.height + "px";
                    }
                    // Also set wrapper min-height as fallback once skeleton is removed
                    if (wrapper) {
                        wrapper.style.minHeight = payload.height + "px";
                    }
                }
            } else if (type === "WIDGET_READY" || type === "HALLIDAY_READY") {
                // Fade out skeleton when widget is ready
                removeEmbedSkeleton();
            }
        };
        window.addEventListener("message", messageHandler);
    } else if (windowType === "MODAL") {
        let iframe = document.getElementById("halliday-payments-modal") as HTMLIFrameElement;
        const existingLocation = iframe ? iframe.src : null;
        if (existingLocation === widgetUrl) {
            iframe.style.display = "block";
        } else {
            if (iframe) {
                iframe.remove();
            }
            // Create iframe
            iframe = document.createElement("iframe");
            iframe.id = "halliday-payments-modal";
            iframe.src = widgetUrl;
            iframe.style.position = "fixed";
            iframe.style.top = "0";
            iframe.style.left = "0";
            iframe.style.width = "100dvw";
            iframe.style.height = "100dvh";
            iframe.style.border = "none";
            iframe.style.zIndex = "1000";
            iframe.setAttribute("scrolling", "no");
            document.body.appendChild(iframe);
        }

        const closeMessageHandler = (event: MessageEvent) => {
            // Verify message origin
            if (event.origin !== windowOrigin || event.source !== paymentsWindow) {
                return;
            }

            const { type } = event.data || {};

            if (type === MessageType.EVENT_WINDOW_CLOSE) {
                window.removeEventListener("message", closeMessageHandler);
                const modal = document.getElementById("halliday-payments-modal");
                if (modal) {
                    modal.style.display = "none";
                }
            }
        };
        window.addEventListener("message", closeMessageHandler);

        paymentsWindow = iframe.contentWindow;
    } else {
        paymentsWindow = window.open(
            widgetUrl,
            "Halliday Payments",
            `popup left=${left} top=${top} width=${width} height=${height}`
        );
    }

    return paymentsWindow;
};

/**
 * Open the provider widget in a popup window.
 *
 * @param {string} url {{string}} The URL for the provider widget.
 * @returns The window object for the provider widget.
 */
export const _openProviderWidget = (url: string) => {
    const width = WIDGET_WIDTH;
    const height = WIDGET_HEIGHT;
    const windowWidth = window.innerWidth;

    const left = windowWidth / 2 - width / 2;
    const top = 70;

    return window.open(url, "Provider Widget", `popup left=${left} top=${top} width=${width} height=${height}`);
};

/**
 * Fetch the payment status.
 * Retries up to 3 times with exponential backoff when the request fails.
 *
 * XXX TODO: Remove or update this function to hit the new v2 endpoint.
 *
 * @param {string} apiKey {{string}} The API key for the payments API.
 * @param {string} baseURL {{string}} The base URL for the payments API.
 * @param {string} workflowId {{string}} The ID of the workflow to fetch the status for.
 * @returns {Promise<PaymentStatus>} Promise that resolves to the {@link PaymentStatus}.
 */
export const _fetchPaymentStatus = async ({
    apiKey,
    baseURL,
    workflowId,
}: {
    apiKey: string;
    baseURL: string;
    workflowId: string;
}): Promise<PaymentStatus> => {
    const maxRetries = 3;
    let lastError: Error;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const response = await fetch(`${baseURL}/quotes/status?workflow_id=${workflowId}`, {
                headers: {
                    Authorization: `Bearer ${apiKey}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));

            if (attempt === maxRetries) {
                throw lastError;
            }

            // Wait before retrying (exponential backoff)
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }

    throw lastError!;
};

/**
 * Wait for the payment to be submitted or the window to be closed or fetch fails.
 *
 * @param {string} apiKey {{string}} The API key for the payments API.
 * @param {string} baseURL {{string}} The base URL for the payments API.
 * @param {Window} window {{Window}} The window object for the payments widget.
 * @param {string} workflowId {{string}} The workflow ID of the payment to fetch the status for.
 */
export const _waitForPaymentStatus = async ({
    apiKey,
    baseURL,
    window,
    workflowId,
}: {
    apiKey: string;
    baseURL: string;
    window: Window;
    workflowId: string;
}) => {
    while (!window.closed) {
        let statusResponse: PaymentStatus;
        try {
            statusResponse = await _fetchPaymentStatus({ apiKey, baseURL, workflowId });
        } catch (error) {
            console.error("Error fetching payment status", error);
            break;
        }
        const observedFundingStep = statusResponse.fulfilled.route.find((detail) => detail.type === "ONRAMP");
        const observedFundingStepStatus = observedFundingStep?.status;
        if (observedFundingStepStatus === "COMPLETE") {
            // Wait for payment being completed
            break;
        }
        await new Promise((resolve) => setTimeout(resolve, 5000));
    }
};

export const logError = (error: unknown) => {
    if (error instanceof ZodError) {
        error.issues.forEach((issue) => {
            console.error("Validation Error", {
                path: issue.path.join("."),
                message: issue.message,
            });
        });
    } else {
        console.error("openHallidayPayments Error", { error });
    }
};
